import React, { createContext, useContext, useState, ReactNode } from "react";
import { DiscoveryRequestTaskResponseService } from "../services/DiscoveryRequestTaskResponseService";
import { DiscoveryRequestTaskResponse } from "../models/DiscoveryRequestTaskResponse";
import { useAuth } from "./AuthContext";
import { Filter } from "../models/Filter";
import { useMatter } from "./MatterContext";
import { clean } from "../utils/utils";
import { File } from "../models/File";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface DiscoveryRequestTaskResponseContextProps {
  selectedDiscoveryRequestTaskResponse: DiscoveryRequestTaskResponse | null;
  createDiscoveryRequestTaskResponse: (
    file: globalThis.File | null,
    data: Omit<
      DiscoveryRequestTaskResponse,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "updatedById"
    >,
  ) => Promise<string>;
  updateDiscoveryRequestTaskResponse: (
    id: string,
    data: Partial<Omit<DiscoveryRequestTaskResponse, "_id" | "createdAt">>,
  ) => Promise<string>;
  deleteDiscoveryRequestTaskResponseById: (id: string) => Promise<void>;
  selectDiscoveryRequestTaskResponseById: (id: string) => Promise<void>;
  loading: Loadings;
  error: Errors;
}

const DiscoveryRequestTaskResponseContext = createContext<
  DiscoveryRequestTaskResponseContextProps | undefined
>(undefined);

export const useDiscoveryRequestTaskResponse =
  (): DiscoveryRequestTaskResponseContextProps => {
    const context = useContext(DiscoveryRequestTaskResponseContext);
    if (!context) {
      throw new Error(
        "useDiscoveryRequestTaskResponse must be used within a DiscoveryRequestTaskResponseProvider",
      );
    }
    return context;
  };

export const DiscoveryRequestTaskResponseProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [
    selectedDiscoveryRequestTaskResponse,
    setSelectedDiscoveryRequestTaskResponse,
  ] = useState<DiscoveryRequestTaskResponse | null>(null);
  const [
    selectedDiscoveryRequestTaskResponseId,
    setSelectedDiscoveryRequestTaskResponseId,
  ] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: false,
    details: false,
  });

  useSocketEventByIds(
    "discoveryrequesttasks:changed",
    ["*"],
    async (updatedDocument) => {
      if (updatedDocument._id === selectedDiscoveryRequestTaskResponseId) {
        const discoveryRequestTask =
          await DiscoveryRequestTaskResponseService.getDiscoveryRequestTaskResponse(
            selectedDiscoveryRequestTaskResponseId!,
          );
        setSelectedDiscoveryRequestTaskResponse(discoveryRequestTask);
      }
    },
  );

  const selectDiscoveryRequestTaskResponseById = async (id: string) => {
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      setErrors((prev) => ({ ...prev, details: null }));
      const discoveryRequestTaskResponse =
        await DiscoveryRequestTaskResponseService.getDiscoveryRequestTaskResponse(
          id,
        );
      setSelectedDiscoveryRequestTaskResponse(discoveryRequestTaskResponse);
    } catch (err: any) {
      setSelectedDiscoveryRequestTaskResponse(null);
      setErrors((prev) => ({ ...prev, details: err }));
    } finally {
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createDiscoveryRequestTaskResponse = async (
    file: globalThis.File | null,
    data: Omit<
      DiscoveryRequestTaskResponse,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "updatedById"
    >,
  ): Promise<string> => {
    if (!user) throw new Error("User not authenticated");
    return DiscoveryRequestTaskResponseService.createDiscoveryRequestTaskResponse(
      file,
      {
        ...data,
        matterId: currentMatterId!,
      },
    );
  };
  //
  const updateDiscoveryRequestTaskResponse = async (
    id: string,
    data: Partial<Omit<DiscoveryRequestTaskResponse, "_id" | "createdAt">>,
  ): Promise<string> => {
    await DiscoveryRequestTaskResponseService.updateDiscoveryRequestTaskResponse(
      id,
      clean(data),
    );
    if (id === selectedDiscoveryRequestTaskResponseId) {
      await selectDiscoveryRequestTaskResponseById(id);
    }
    return id;
  };

  const deleteDiscoveryRequestTaskResponseById = async (
    id: string,
  ): Promise<void> => {
    if (!currentMatterId) return;
    await DiscoveryRequestTaskResponseService.deleteDiscoveryRequestTaskResponse(
      id,
    );
    if (id === selectedDiscoveryRequestTaskResponseId) {
      setSelectedDiscoveryRequestTaskResponse(null);
      setSelectedDiscoveryRequestTaskResponseId(null);
    }
  };

  return (
    <DiscoveryRequestTaskResponseContext.Provider
      value={{
        selectedDiscoveryRequestTaskResponse,
        selectDiscoveryRequestTaskResponseById,
        loading: loadings,
        error: errors,
        createDiscoveryRequestTaskResponse,
        updateDiscoveryRequestTaskResponse,
        deleteDiscoveryRequestTaskResponseById,
      }}
    >
      {children}
    </DiscoveryRequestTaskResponseContext.Provider>
  );
};
